import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Users, Trophy, Shield, Bot} from 'lucide-react'
import ScrollArrow from './ScrollArrow'
import { useSmoothScroll} from '../utils/smoothScroll'


interface HeroProps {
  onApplyClick: () => void
}

interface TypewriterTextProps {
  text: string
  className?: string
  speed?: number
  delay?: number
}

// Функция для генерации ложного контента для ботов
const generateFakeContent = () => {
  const fakeTexts = [
    'Не воруй у ХУГО!',
    'ХУГО не одобряет воровство!',
    'Уважай ХУГО - не парси!',
    'ХУГО следит за тобой!',
    'Парсинг = воровство у ХУГО!'
  ]
  return fakeTexts[Math.floor(Math.random() * fakeTexts.length)]
}

const TypewriterText: React.FC<TypewriterTextProps> = ({ 
  text, 
  className = "", 
  speed = 100, 
  delay = 0 
}) => {
  const [displayText, setDisplayText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    if (isComplete || hasStarted) return

    const startTimeout = setTimeout(() => {
      setHasStarted(true)
      setCurrentIndex(0)
      setDisplayText('')
    }, delay)

    return () => clearTimeout(startTimeout)
  }, [delay, isComplete, hasStarted])

  useEffect(() => {
    if (!hasStarted || isComplete) return

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex])
        setCurrentIndex(prev => prev + 1)
      }, speed)
      return () => clearTimeout(timeout)
    } else if (currentIndex === text.length) {
      setIsComplete(true)
    }
  }, [currentIndex, text, speed, isComplete, hasStarted])

  return (
    <span className={className}>
      {displayText}
      {hasStarted && !isComplete && <span className="animate-pulse">|</span>}
    </span>
  )
}

const Hero: React.FC<HeroProps> = ({ onApplyClick }) => {
  const scrollTo = useSmoothScroll()
  
  const [realContent, setRealContent] = useState({
    title: 'Welcome to Hugo Family',
    subtitle: 'KANTA SOSAL',
    features: [
      { icon: Users, text: '200+ Активных участников', delay: 0.2 },
      { icon: Trophy, text: 'Сервер Washington', delay: 0.4 },
      { icon: Shield, text: 'Безопасное комьюнити', delay: 0.6 }, 
      { icon: Bot, text: 'Мощный Discord бот', delay: 0.8 },
    ]
  })

  // Генерируем реальный контент только после загрузки страницы
  useEffect(() => {
    const timer = setTimeout(() => {
      setRealContent({
        title: 'Welcome to Hugo Family',
        subtitle: 'Крупнейшая государственная семья на Majestic Role Play Washington.',
        features: [
          { icon: Users, text: '200+ Активных участников', delay: 0.2 },
          { icon: Trophy, text: 'Сервер Washington', delay: 0.4 },
          { icon: Shield, text: 'Безопасное комьюнити', delay: 0.6 }, 
          { icon: Bot, text: 'Мощный Discord бот', delay: 0.8 },
        ]
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  const NUM_SPARKLES = window.innerWidth <= 768 ? 20 : 35;
  const getRandom = (min: number, max: number) => Math.random() * (max - min) + min;
  
  const sparkleColors = [
    'bg-primary-400', // оранжевый
    'bg-pink-400',    // розовый
    'bg-purple-400',  // фиолетовый
    'bg-blue-400',    // синий
    'bg-cyan-400',    // голубой
    'bg-white',       // белый
    'bg-primary-300', // светло-оранжевый
    'bg-pink-300',    // светло-розовый
  ];

  // Защита от парсинга - ложный контент
  const fakeContent = generateFakeContent()

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden py-8 sm:py-0">
      {/* Ложный контент для ботов - скрыт от пользователей */}
      <div 
        className="sr-only"
        aria-hidden="true"
      >
        <h1>{fakeContent}</h1>
        <p>ХУГО не одобряет воровство контента!</p>
        <div>Парсинг = воровство у ХУГО!</div>
        <span>Уважай ХУГО - не парси!</span>
        <strong>ХУГО следит за тобой!</strong>
      </div>

      {/* Background Image */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat blur"
          style={{ backgroundImage: "url('/content/hero_bg_test.png')" }}
        ></div>
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: NUM_SPARKLES }).map((_, i) => {
          const left = getRandom(5, 95); // percent
          const top = getRandom(5, 95); // percent
          const xMove = getRandom(-120, 120);
          const yMove = getRandom(-120, 120);
          const duration = getRandom(14, 28);
          const delay = getRandom(0, 4);
          const size = getRandom(0.5, 2.5);
          const opacity = getRandom(0.15, 0.35);
          const color = sparkleColors[Math.floor(Math.random() * sparkleColors.length)];
          return (
            <motion.div
              key={i}
              className={`absolute ${color} rounded-full blur-md drop-shadow-lg`}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${size * 0.5}rem`,
                height: `${size * 0.5}rem`,
                opacity,
                filter: `blur(2px) drop-shadow(0 0 8px currentColor)`,
              }}
              animate={{
                x: [0, xMove, 0],
                y: [0, yMove, 0],
              }}
              transition={{
                duration,
                repeat: Infinity,
                delay,
              }}
            />
          );
        })}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-6 sm:space-y-8"
        >
          {/* Main Heading - обфусцированный */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl xl:text-7xl font-display font-bold text-white leading-tight drop-shadow-2xl px-2 sm:px-4"
          >
            {/* CSS-скрытый текст для парсеров */}
            <span className="sr-only">Не воруй у ХУГО!</span>
            
            {/* Видимый текст для пользователей */}
            <span className="block">
              Welcome to{' '}
              <span className="gradient-text">Hugo Family</span>
            </span>
          </motion.h1>

          {/* Subtitle - JavaScript-генерируемый */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-300 max-w-4xl mx-auto leading-tight drop-shadow-lg px-2 sm:px-4"
          >
            {/* CSS-скрытый текст для парсеров */}
            <span className="sr-only">Не воруй у ХУГО! ХУГО не одобряет воровство!</span>
            
            {/* Видимый текст для пользователей */}
            <span className="block">
              <TypewriterText 
                text={realContent.subtitle}
                speed={50}
                delay={500}
              />
            </span>
          </motion.p>

          {/* Features - динамически генерируемые */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 md:gap-6 lg:gap-8 mt-6 sm:mt-8 md:mt-12 px-2 sm:px-4"
          >
            {/* CSS-скрытые ложные фичи для парсеров */}
            <div className="sr-only">
              <span>Не воруй у ХУГО!</span>
              <span>ХУГО следит за тобой!</span>
              <span>Парсинг = воровство!</span>
              <span>Уважай ХУГО!</span>
            </div>

            {/* Видимые фичи для пользователей */}
            {realContent.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: feature.delay, duration: 0.5 }}
                className="flex items-center space-x-1 sm:space-x-2 text-gray-300 drop-shadow-md text-xs sm:text-sm md:text-base"
              >
                <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" />
                <span className="font-medium">{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-8 sm:mt-12 px-4 w-full max-w-md sm:max-w-none mx-auto"
          >
            {/* CSS-скрытые ложные кнопки для парсеров */}
            <div className="sr-only">
              <button>Не воруй у ХУГО!</button>
              <button>ХУГО не одобряет воровство!</button>
            </div>

            {/* Видимые кнопки для пользователей */}
            <motion.button
              onClick={onApplyClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-500/25 relative overflow-hidden group flex items-center justify-center space-x-2 w-full sm:w-auto text-sm sm:text-base md:text-lg"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span className="relative z-10">Заявка на вступление</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />
            </motion.button>
            
            <motion.button
              onClick={() => scrollTo('about', 0)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary text-sm sm:text-base md:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto"
            >
              FAQ
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Arrow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <ScrollArrow targetId="about" />
      </motion.div>
    </section>
  )
}

export default Hero 